import { Fragment } from "react";
import { TextField, MenuItem, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { resolvePayheadAmounts, previewPfAmount, rowKey, DEDUCTION_PARENT_CATEGORY_IDS, EMPLOYEE_DEDUCTION_PARENT_CATEGORY_ID } from "./payheadUtils.js";
import PfWageBaseComposer from "./PfWageBaseComposer.jsx";

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;

const METHOD_OPTIONS = [
  { label: "Not Applicable", value: "NOT_APPLICABLE" },
  { label: "Fixed", value: "FIXED" },
  { label: "Percentage", value: "PERCENTAGE" },
];

const CAL_FROM_OPTIONS = [
  { label: "% of Rate", value: "RATE" },
  { label: "% of Basic", value: "BASIC" },
  // Sum of every Standard Monthly Salary + Extra Earnings payhead -
  // only meaningful (and only offered) for an Employer/Employee
  // Deduction payhead, e.g. PF/ESI calculated on total earnings.
  { label: "% of Gross", value: "GROSS" },
];

/**
 * Every active payhead (Basic, HRA, PF, PT...) is shown up front, grouped
 * by section, each with a Not Applicable / Fixed / Percentage selector -
 * this mirrors CI4's own payroll-structure screen exactly
 * (payroll_template_form.php / ajax_employee_payroll.php: every category
 * renders as a row immediately, no search-and-add dropdown, since
 * scrolling a short grouped list reads faster than typing to filter one).
 *
 * `rows` / `onChange` still only carry the SELECTED (Fixed/Percentage)
 * payheads - the exact same shape as before - "Not Applicable" is simply
 * a category with no matching row, not a value stored anywhere.
 *
 * baseRateAmount: pass the wage rate's rate_amount to preview a live
 * total against it; omit it entirely on a template (which isn't tied to
 * any one rate) and the total line won't render.
 *
 * categories: the active salary-category list (see
 * useActiveSalaryCategories) - owned by the parent form so it can be
 * reused for validation too, not fetched independently here.
 *
 * pfRule: the parent form's PF Rule state (pf_rule_type and friends - see
 * PfRuleCard.jsx on the Payroll Template form / the read-only inherited
 * summary on AddEditWageRate.jsx). Passed through so the "PF" checkbox
 * column, the live Amount preview, and the PF Wage Base composer (below)
 * for the is_pf row can render. Omit pfRule entirely on a screen with no
 * PF Rule concept at all.
 *
 * onPfRuleChange(patch): merges a patch into the parent's pfRule state -
 * only ever called to update pf_wage_base_category_ids, from the PF Wage
 * Base composer that appears right under the is_pf row whenever ITS OWN
 * "Based On" is Basic, or a configured PF Rule uses Basic somewhere. This
 * is deliberately NOT gated on a formal Rule being configured - real PF
 * wages (Basic + DA + Retaining Allowance, see wageRateComponents.js's
 * applyPfRule) apply to the is_pf row's own manual %-of-Basic calculation
 * too, with or without a template/Rule (e.g. creating a brand new wage
 * rate with no template still needs this). Omit onPfRuleChange to make
 * the composer read-only-hidden even if pfRule is passed (there's nothing
 * useful to show without a way to edit it).
 */
const PayheadRowsEditor = ({
  rows,
  onChange,
  categories,
  baseRateAmount,
  error,
  pfRule = null,
  onPfRuleChange = null,
  title = "Salary Components (optional)",
  helpText = "Set each payhead to Fixed or Percentage - the rest stay Not Applicable. Each is pro-rated the same way the flat rate is.",
}) => {
  // A row is matched to a category by category_id first - category_name
  // alone is ambiguous when two categories share a name across sections
  // (e.g. "Provident Fund" under both Employer and Employee Deduction).
  // Only a row saved before category_id existed falls back to a name
  // match; that's inherently ambiguous for a pre-existing duplicate-name
  // pair, but resolves itself the moment the row is next edited (every
  // upsert below always stamps category_id going forward).
  const rowFor = (category) =>
    rows.find((r) => (r.category_id != null ? r.category_id === category.id : r.category_name === category.category_name));

  const upsertRow = (category, patch) => {
    const existing = rowFor(category);
    if (existing) {
      onChange(rows.map((r) => (r === existing ? { ...r, ...patch } : r)));
    } else {
      const sortOrder = categories.findIndex((c) => c.id === category.id);
      onChange([
        ...rows,
        {
          category_name: category.category_name,
          category_id: category.id,
          cal_method: "FIXED",
          cal_value: "",
          cal_from: "RATE",
          is_basic: false,
          sort_order: sortOrder,
          ...patch,
        },
      ]);
    }
  };

  const setMethod = (category, method) => {
    if (method === "NOT_APPLICABLE") {
      const existing = rowFor(category);
      onChange(rows.filter((r) => r !== existing));
      return;
    }

    const patch = { cal_method: method };
    // The "Based On" dropdown's value falls back to "RATE" for DISPLAY
    // only (see its own value={row?.cal_from || "RATE"} below) - it never
    // writes that fallback back into the row. A brand-new row picks up
    // "RATE" from upsertRow's own object-literal default below, but an
    // EXISTING row switched straight from Fixed to Percentage (cal_from
    // still null, since Fixed never needed one) would otherwise keep
    // cal_from null despite the dropdown visually showing "% of Rate" as
    // selected - failing validation ("no valid base") until the admin
    // happens to re-touch that same dropdown. Stamp it explicitly here so
    // what's displayed always matches what's actually stored.
    if (method === "PERCENTAGE") {
      const existing = rowFor(category);
      if (!["RATE", "BASIC", "GROSS"].includes(existing?.cal_from)) {
        patch.cal_from = "RATE";
      }
    }
    upsertRow(category, patch);
  };

  // Basic can be Fixed or a % of Rate - it just can't be a % of Basic
  // (itself), so that option drops out of its own Cal From list. Clear
  // cal_from only if it was pointing at the now-impossible "BASIC".
  const setBasicRow = (category) => {
    const target = rowFor(category);
    onChange(
      rows.map((r) =>
        r === target ? { ...r, is_basic: true, cal_from: r.cal_from === "BASIC" ? "RATE" : r.cal_from } : { ...r, is_basic: false }
      )
    );
  };

  // Only one row can be the PF payhead a PF Rule overrides - same
  // single-row exclusivity as Basic above.
  const setPfRow = (category) => {
    const target = rowFor(category);
    onChange(rows.map((r) => (r === target ? { ...r, is_pf: true } : { ...r, is_pf: false })));
  };

  // Calculated amounts (per payhead, and per-section subtotals) only make
  // sense once there's a concrete Rate Amount to resolve "% of Rate"
  // against - on the Payroll Template form (no baseRateAmount) there's
  // nothing to compute yet, so that column and the subtotal rows just
  // don't render there.
  const showAmounts = baseRateAmount !== undefined;
  const { amountByCategory, total, basicAmount, grossTotal } = showAmounts
    ? resolvePayheadAmounts(rows, baseRateAmount, categories)
    : { amountByCategory: {}, total: null, basicAmount: null, grossTotal: 0 };

  // The is_pf row's Amount, if a PF Rule is active, overrides whatever its
  // own Fixed/Percentage config would otherwise show - see
  // previewPfAmount's own comment for why this doesn't touch `total`.
  const pfPreviewAmount = showAmounts ? previewPfAmount(rows, pfRule, basicAmount, grossTotal, baseRateAmount, amountByCategory) : null;
  const amountFor = (row) => (row?.is_pf && pfPreviewAmount !== null ? pfPreviewAmount : amountByCategory[rowKey(row)]);
  const rateAmount = Number(baseRateAmount) || 0;
  // Rate Amount is a CTC-style total - ALL four sections (Standard
  // Monthly Salary, Extra Earnings, Employer Deduction, Employee
  // Deduction) together must sum to it (see
  // EmployeeWageRateService.validateComponents). computeGrossPay() still
  // pays out rate_amount as Gross and subtracts the deduction payheads
  // to reach Net Pay - that works out to the earnings portion either way.
  const totalMismatch = total !== null && Math.abs(total - rateAmount) > 0.01;

  const sections = [...new Set(categories.map((c) => c.section_name))];

  const basicCategoryId = rows.find((r) => r.is_basic)?.category_id ?? null;
  // A configured PF Rule (FIXED/PERCENTAGE/LIMIT) can reference Basic
  // through its own below/above/percentage base, independently of the
  // is_pf row's own "Based On" - the composer needs to appear for either
  // reason, not just when the row's own dropdown happens to say Basic.
  const pfRuleUsesBasic =
    pfRule?.pf_rule_type &&
    pfRule.pf_rule_type !== "NONE" &&
    (pfRule.pf_percentage_cal_from === "BASIC" || pfRule.pf_below_cal_from === "BASIC" || pfRule.pf_above_cal_from === "BASIC");
  const columnCount = 4 + (showAmounts ? 1 : 0) + 1 + (pfRule ? 1 : 0);

  return (
    <div className="col-12 mb-3">
      <label className="form-label mb-2 d-flex align-items-center gap-1" style={{ fontWeight: 600, fontSize: "12px" }}>
        {title}
        <Tooltip title={helpText}>
          <InfoOutlined sx={{ fontSize: 14, color: "#888" }} />
        </Tooltip>
      </label>

      {sections.map((section) => {
        const sectionCategories = categories.filter((c) => c.section_name === section);
        const sectionTotal = showAmounts
          ? sectionCategories.reduce((sum, c) => {
              const r = rowFor(c);
              return sum + (r ? amountFor(r) || 0 : 0);
            }, 0)
          : 0;

        return (
          <div className="card border mb-3" key={section}>
            <div className="card-header bg-light py-2" style={{ fontWeight: 600, fontSize: "12px" }}>
              {section}
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0 align-middle">
                <thead>
                  <tr style={{ fontSize: "11px" }} className="text-muted">
                    <th>Payhead</th>
                    <th style={{ width: "160px" }}>Method</th>
                    <th style={{ width: "110px" }}>Value</th>
                    <th style={{ width: "130px" }}>Based On</th>
                    {showAmounts && <th style={{ width: "110px" }}>Amount</th>}
                    <th style={{ width: "60px" }}>Basic</th>
                    {pfRule && <th style={{ width: "60px" }}>PF</th>}
                  </tr>
                </thead>
                <tbody>
                  {sectionCategories.map((category) => {
                    const row = rowFor(category);
                    const method = row?.cal_method || "NOT_APPLICABLE";
                    // Shown right under the is_pf row whenever "Basic"
                    // means something for it - either its own manual
                    // %-of-Basic config, or a configured PF Rule that
                    // uses Basic somewhere (the rule overrides the row's
                    // amount, but still shares this same wage base). See
                    // PayheadRowsEditor's own prop docs for why this
                    // isn't gated on a formal Rule being configured.
                    const showWageBaseComposer =
                      pfRule &&
                      onPfRuleChange &&
                      Boolean(row?.is_pf) &&
                      ((method === "PERCENTAGE" && row.cal_from === "BASIC") || pfRuleUsesBasic);
                    return (
                      <Fragment key={category.id}>
                        <tr>
                          <td style={{ fontSize: "13px" }}>{category.category_name}</td>
                          <td>
                            <TextField select size="small" fullWidth value={method} onChange={(e) => setMethod(category, e.target.value)}>
                              {METHOD_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </td>
                          <td>
                            {method !== "NOT_APPLICABLE" && (
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                slotProps={{ htmlInput: { min: 0 } }}
                                placeholder={method === "PERCENTAGE" ? "%" : "Amount"}
                                value={row?.cal_value ?? ""}
                                onChange={(e) => upsertRow(category, { cal_value: e.target.value })}
                              />
                            )}
                          </td>
                          <td>
                            {method === "PERCENTAGE" && (
                              <Tooltip title={row?.is_basic ? "Basic can only be a % of Rate - it can't be a % of itself or of Gross" : ""}>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  value={row?.cal_from || "RATE"}
                                  onChange={(e) => upsertRow(category, { cal_from: e.target.value })}
                                >
                                  {CAL_FROM_OPTIONS.filter(
                                    (opt) =>
                                      !(row?.is_basic && (opt.value === "BASIC" || opt.value === "GROSS")) &&
                                      !(opt.value === "GROSS" && !DEDUCTION_PARENT_CATEGORY_IDS.includes(category.parent_category_id))
                                  ).map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Tooltip>
                            )}
                          </td>
                          {showAmounts && (
                            <td className="fw-semibold" style={{ fontSize: "13px" }}>
                              {method !== "NOT_APPLICABLE" && money(amountFor(row))}
                            </td>
                          )}
                          <td className="text-center">
                            {method !== "NOT_APPLICABLE" && (
                              <FormControlLabel
                                control={<Checkbox size="small" checked={Boolean(row?.is_basic)} onChange={() => setBasicRow(category)} />}
                                label=""
                                style={{ margin: 0 }}
                              />
                            )}
                          </td>
                          {pfRule && (
                            <td className="text-center">
                              {method !== "NOT_APPLICABLE" && category.parent_category_id === EMPLOYEE_DEDUCTION_PARENT_CATEGORY_ID && (
                                <Tooltip title="The one Employee Deduction payhead a PF Rule (if configured) overrides - otherwise this row is just calculated normally, from its own Method/Value/Based On">
                                  <FormControlLabel
                                    control={<Checkbox size="small" checked={Boolean(row?.is_pf)} onChange={() => setPfRow(category)} />}
                                    label=""
                                    style={{ margin: 0 }}
                                  />
                                </Tooltip>
                              )}
                            </td>
                          )}
                        </tr>
                        {showWageBaseComposer && (
                          <tr>
                            <td colSpan={columnCount} className="pt-0">
                              <PfWageBaseComposer
                                wageBaseIds={pfRule.pf_wage_base_category_ids}
                                onChange={(ids) => onPfRuleChange({ pf_wage_base_category_ids: ids })}
                                categories={categories}
                                basicCategoryId={basicCategoryId}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                {showAmounts && (
                  <tr className="fw-semibold" style={{ fontSize: "13px" }}>
                    <td colSpan={3}>Subtotal - {section}</td>
                    <td></td>
                    <td>{money(sectionTotal)}</td>
                    <td></td>
                    {pfRule && <td></td>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        );
      })}

      {total !== null && rows.length > 0 && (
        <div className={`form-text ${totalMismatch ? "text-danger" : ""}`}>
          Components total: ₹{total.toFixed(2)}
          {totalMismatch
            ? ` - must match Rate Amount (₹${rateAmount.toFixed(2)}) before this can be saved.`
            : " - matches Rate Amount above."}
        </div>
      )}
      {error && <div className="form-text text-danger">{error}</div>}
    </div>
  );
};

export default PayheadRowsEditor;
